import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RestaurantDiningRoomDTO } from '../../../types/restaurant/RestaurantDiningRoomTypes';
import {
  RestaurantTableDTO,
  RestaurantTableForm,
  RestaurantTablePage,
} from '../../../types/restaurant/RestaurantTableTypes';

export const emptyRestaurantTableForm: RestaurantTableForm = {
  diningRoomId: '',
  code: '',
  name: '',
  capacity: 4,
  status: 'FREE',
  shape: 'SQUARE',
  positionX: '',
  positionY: '',
  displayOrder: 1,
};

interface State {
  list: RestaurantTableDTO[];
  page: RestaurantTablePage | null;
  diningRooms: RestaurantDiningRoomDTO[];
  form: RestaurantTableForm;
  loading: boolean;
  updating: boolean;
  error: string;
  saving: boolean;
  modalOpen: boolean;
}

const initialState: State = {
  list: [],
  diningRooms: [],
  page: null,
  form: emptyRestaurantTableForm,
  loading: false,
  updating: false,
  error: '',
  saving: false,
  modalOpen: false,
};

const slice = createSlice({
  name: 'restaurantTable',
  initialState,
  reducers: {
    setRestaurantTables(state, action: PayloadAction<RestaurantTableDTO[]>) {
      state.list = action.payload;
    },
    setRestaurantTablePage(
      state,
      action: PayloadAction<RestaurantTablePage | null>
    ) {
      state.page = action.payload;
      state.list = action.payload?.content ?? [];
    },
    setTableDiningRooms(
      state,
      action: PayloadAction<RestaurantDiningRoomDTO[]>
    ) {
      state.diningRooms = action.payload;
    },
    setRestaurantTableLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setRestaurantTableUpdating(state, action: PayloadAction<boolean>) {
      state.updating = action.payload;
    },
    setRestaurantTableError(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    resetRestaurantTableResults(state) {
      state.list = [];
      state.diningRooms = [];
      state.page = null;
      state.loading = false;
      state.updating = false;
      state.error = '';
    },
    setRestaurantTableSaving(state, action: PayloadAction<boolean>) {
      state.saving = action.payload;
    },
    openRestaurantTableCreate(state) {
      state.form = { ...emptyRestaurantTableForm };
      state.modalOpen = true;
    },
    openRestaurantTableEdit(state, action: PayloadAction<RestaurantTableDTO>) {
      const table = action.payload;
      state.form = {
        id: table.id,
        diningRoomId: table.diningRoomId ?? '',
        code: table.code,
        name: table.name,
        capacity: table.capacity,
        status: table.status,
        shape: table.shape,
        positionX: table.positionX ?? '',
        positionY: table.positionY ?? '',
        displayOrder: table.displayOrder,
        active: table.active,
      };
      state.modalOpen = true;
    },
    closeRestaurantTableModal(state) {
      state.form = { ...emptyRestaurantTableForm };
      state.modalOpen = false;
    },
    updateRestaurantTableField(
      state,
      action: PayloadAction<{ field: keyof RestaurantTableForm; value: any }>
    ) {
      state.form = {
        ...state.form,
        [action.payload.field]: action.payload.value,
      };
    },
  },
});

export const {
  setRestaurantTables,
  setRestaurantTablePage,
  setTableDiningRooms,
  setRestaurantTableLoading,
  setRestaurantTableUpdating,
  setRestaurantTableError,
  resetRestaurantTableResults,
  setRestaurantTableSaving,
  openRestaurantTableCreate,
  openRestaurantTableEdit,
  closeRestaurantTableModal,
  updateRestaurantTableField,
} = slice.actions;
export default slice.reducer;
